<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { getFormattedCurrency } from "@/shared/utils/helpers";

interface PayabliEmbeddedCreditCardProps {
  apiToken: string;
  entryPointName: string;
  temporaryMethodToken: boolean;
  creditCardFormValid: boolean;
  amount: number;
  onPay: () => void;
  loadScript: () => Promise<void>;
  reInitializePayabliComponent: () => void;
  authenticated: boolean;
  canAcceptPayment?: boolean;
  selectedPaymentOption?: string;
  /**
   * Indicates an external loading or processing state, such as when an API
   * request is pending after the user has clicked Pay. When true, the Pay
   * button will be disabled and the user will see a "Processing..." label in
   * place of the normal button text. Defaults to false so existing usages are
   * unaffected.
   */
  loading?: boolean;
}

const props = withDefaults(defineProps<PayabliEmbeddedCreditCardProps>(), {
  canAcceptPayment: true,
  loading: false,
});

const { loadScript, reInitializePayabliComponent, onPay } = props;

const isProcessing = ref(false);

const enablePayButton = computed(() => {
  return (
    props.creditCardFormValid &&
    !isProcessing.value &&
    !props.loading &&
    props.canAcceptPayment
  );
});

const handlePay = () => {
  try {
    isProcessing.value = true;
    onPay();
  } finally {
    isProcessing.value = false;
  }
};

onMounted(() => {
  loadScript();
  reInitializePayabliComponent();
});
</script>

<template>
  <div>
    <div id="pay-component-1" />
    <div class="items-center pt-2">
      <button
        id="btnx"
        type="button"
        class="bg-blue-400 text-sm p-2 font-bold rounded w-full"
        :class="{
          'opacity-50 cursor-not-allowed': !enablePayButton,
          'payment-installment-button':
            props.selectedPaymentOption === 'payment-installment',
        }"
        :disabled="!enablePayButton"
        @click="handlePay"
      >
        <template v-if="props.selectedPaymentOption === 'payment-installment'">
          <div class="flex flex-col items-center py-1">
            <span>Save</span>
            <span class="text-xs">and agree to future billing</span>
          </div>
        </template>
        <template v-else>
          <span
            v-if="isProcessing || props.loading"
            class="flex items-center justify-center gap-2"
          >
            <i class="pi pi-spin pi-spinner text-xs"></i>
            Processing...
          </span>
          <span v-else>Save</span>
        </template>
      </button>
    </div>
  </div>
</template>

<style scoped>
/* TODO: Make these Global styles, also seen Continue button */
.resend-email-button {
  background-color: var(--surface-overlay);
  border-color: var(--surface-overlay);
  color: #a3a3a3;
  width: 10rem;
}

.save-button {
  color: black;
  width: 10rem;
}

.payment-installment-button {
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
}
</style>
